import {Checkbox, InputAdornment, Paper, Popper} from '@mui/material';
import React, {FunctionComponent, useEffect, useMemo, useRef, useState} from 'react';
import {SubmitHandler, useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router';
import Swal from 'sweetalert2';
import {
  CheckboxInputChecked,
  CheckboxInputEmpty,
  CheckboxInputError,
  errorBigIcon,
  errorStatusIcon,
  gLogo,
  iconNotifySuccess,
  lineArrowRight,
} from '../../../assets/icons/icons';
import IsVisibleButton from '../../../components/Buttons/IsVisibleButton/IsVisibleButton';
import {AuthWrapper as Wrapper, Button, FormControl, TextField} from '../../../components/StyledComponents';
import {
  EActivePlatformsNames,
  EMAIL_REGEX,
  MAX_INPUT_VALUE_EMAIL_LENGTH,
  MAX_INPUT_VALUE_LENGTH,
  MIN_INPUT_VALUE_LENGTH,
  PASSWORD_REGEX
} from '../../../models/consts';
import {IMarketingData, IPasswordsVisiblity, ISignUp} from '../../../models/inner-models';
import {ISignUpApi} from '../../../models/models';
import {postDataToMarketing, registerCognitoUser} from '../../../store/actions';
import {useAppDispatch} from '../../../store/hooks';
import {decoratePopUpMessage} from '../../../utils/popUpTextDecorator';
import {Helmet} from 'react-helmet';
// import '../style.scss';
import './style.scss';
import {signInWithRedirect} from 'aws-amplify/auth';
import {iniFrame} from "../../../serviceWorker";
import {zxcvbn, zxcvbnOptions} from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import {PLATFORM_ICONS} from './constants';
import {syncChoosenAppCookie} from '../../../utils/choosenAppCookie';
import AuthRightAnimation from "../../../components/AuthRightAnimation";
import AuthHeader from "../../../components/Header/AuthHeader";
import {executeRecaptcha, isRecaptchaEnabled, loadRecaptchaScript} from '../../../utils/recaptcha';

const env = process.env.STAGE_TYPE;
const ALLOWED_ACTIVE_CAMPAIGN_ENVS = ['prod']

const SignUp: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {t: translate} = useTranslation();
  const {register, formState: {errors}, handleSubmit, control, setValue} = useForm<ISignUp>({
    mode: 'onChange',
    defaultValues: {
      appsProtect: EActivePlatformsNames.map(() => false),
      termsAccepted: false,
      marketingAccepted: false,
    },
  });
  const [notValidError, setNotValidError] = useState<boolean>(true);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [isGoogleAuth, setIsGoogleAuth] = useState<boolean>(false);
  const mainPass = useWatch({control, name: 'password'});
  const passwordValue = mainPass ?? '';
  const appsProtect = useWatch({control, name: 'appsProtect'}) || [];
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const passwordAnchorRef = useRef<HTMLDivElement | null>(null);
  const termsConsentRef = useRef<HTMLDivElement | null>(null);

  const [visible, setPasswordVisibility] = useState<IPasswordsVisiblity>({
    password: false
  });

  const options = {
    translations: zxcvbnEnPackage.translations,
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
    },
  }
  zxcvbnOptions.setOptions(options)

  const passwordHelpListClassName = useMemo(() => {
    return passwordValue.length ? 'auth-password-help' : 'auth-password-help validation';
  }, [passwordValue.length]);

  const zxcvbnResult = useMemo(() => {
    return passwordValue.length ? zxcvbn(passwordValue) : null;
  }, [passwordValue]);
  const termsErrorMessage = errors.termsAccepted?.message as string | undefined;

  const handleClickShowPassword = (field: keyof IPasswordsVisiblity) => () => {
    setPasswordVisibility({...visible, [field]: !visible[field]});
  };

  const togglePlatform = (index: number) => {
    const nextState = EActivePlatformsNames.map((_, i) => Boolean(appsProtect?.[i]));
    nextState[index] = !nextState[index];
    setValue('appsProtect', nextState, {shouldDirty: true, shouldTouch: true});
  };

  useEffect(() => {
    if (isRecaptchaEnabled()) {
      loadRecaptchaScript().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const hasDictionaryWords = Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "dictionary"));
    const hasWeakRepeat = Boolean(
      zxcvbnResult?.sequence?.some(i => i.pattern === "repeat") && zxcvbnResult?.score === 0
    );

    if (PASSWORD_REGEX.test(passwordValue) && !(hasDictionaryWords || hasWeakRepeat)) {
      setNotValidError(false);
    } else {
      setNotValidError(true);
    }
  }, [passwordValue, zxcvbnResult]);

  const onSubmit: SubmitHandler<ISignUp> = async ({
                                              firstName,
                                              lastName,
                                              email,
                                              password,
                                              appsProtect,
                                              marketingAccepted,
                                            }) => {
    const appsToProtect = EActivePlatformsNames.filter((_, index) => appsProtect?.[index]);

    if (!notValidError) {
      setIsSubmit(true);

      let recaptchaToken: string | undefined;
      if (isRecaptchaEnabled()) {
        try {
          const token = await executeRecaptcha('signup');
          if (!token) {
            setIsSubmit(false);
            Swal.fire({
              title: translate('notifications.titles.error'),
              text: 'Security verification failed. Please refresh the page and try again.',
              imageUrl: errorBigIcon,
              confirmButtonText: translate('notifications.choices.close'),
            });
            return;
          }
          recaptchaToken = token;
        } catch (err) {
          setIsSubmit(false);
          console.log(err);
          Swal.fire({
            title: translate('notifications.titles.error'),
            text: 'Security verification failed. Please refresh the page and try again.',
            imageUrl: errorBigIcon,
            confirmButtonText: translate('notifications.choices.close'),
          });
          return;
        }
      }

      const signUpFormData: ISignUpApi = {
        email,
        password,
        name: firstName,
        family_name: lastName,
        emailSending: marketingAccepted,
        recaptchaToken,
      };
      const marketingData: IMarketingData = {
        email,
        appsToProtect: appsToProtect
      }

      dispatch(registerCognitoUser(signUpFormData))
        .unwrap()
        .then(() => {
          if (env && ALLOWED_ACTIVE_CAMPAIGN_ENVS.includes(env)) {
            dataLayer.push({
              'event': 'sign_up',
              'method': 'email',
              'signUpProcess': 'success',
              'user_data': {
                'email': email
              },
              'appsToProtect': appsToProtect.join('/')
            })
          }
          return Swal.fire({
            title: translate('notifications.titles.success'),
            text: iniFrame() ? 'Registration successful. Please login to BackupLABS to start protecting your data.' : 'Please check your email now for the confirmation link.',
            imageUrl: iconNotifySuccess,
            confirmButtonText: translate('notifications.choices.continue'),
            customClass: {
              image: 'success-sign-up-icon'
            },
            didOpen: (toast) => {
              if (env && ALLOWED_ACTIVE_CAMPAIGN_ENVS.includes(env)) {
                const children = toast.children
                let notes: Element | null;
                for (const child of children) {
                  if (child.className === 'swal2-actions') {
                    notes = child
                    break;
                  }
                }
                if (notes) {
                  for (const child of notes.children) {
                    if (child.className.includes('swal2-confirm')) {
                      child.id = 'signup_confirm_modal_btn'
                      break;
                    }
                  }
                }
              }
            }
          });
        })
        .then(() => {
          setIsSubmit(false)

          syncChoosenAppCookie(appsToProtect);

          if (env && ALLOWED_ACTIVE_CAMPAIGN_ENVS.includes(env)) {
            dispatch(postDataToMarketing(marketingData))
          }

          navigate('/auth/login');
        })
        .catch((err) => {
          setIsSubmit(false)
          console.log(err);
          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(err.error),
            imageUrl: errorBigIcon,
            confirmButtonText: translate('notifications.choices.close'),
          });
        });
    }
  };
  const googleAuth = async () => {
    if (!isGoogleAuth) {
      setIsGoogleAuth(true)
      if(env && ALLOWED_ACTIVE_CAMPAIGN_ENVS.includes(env)) {
        setCookie('google_sign_up', '1', 3)
      }
      await signInWithRedirect({provider: 'Google'});
    }
  }

  return (
    <>
      <Helmet>
        <title>BackupLABS Account Creation Page</title>
        <meta name="title" content="BackupLABS Account Creation Page"/>
        <meta name="description"
              content="Create a free account on the BackupLABS platform to start protecting your critical SaaS app data."/>
      </Helmet>
      <div className='auth-signup'>
        <div className='auth-signup-left'>
          <AuthHeader/>
          <Wrapper className="signup__wrapper auth-new-form">
            <div className="auth-title">
              {translate('forms.sign_up.title')}
            </div>
            <div className="links-block signup__trial-subtitle">
              {translate('forms.sign_up.subTitle')}
            </div>
            {!iniFrame() && (
              <div
                className={`google-btn auth-oauth-btn ${isGoogleAuth ? 'disabled' : ''}`}
                onClick={googleAuth}
              >
                <div className="google-icon-wrapper">
                  <img className="google-icon-svg" src={gLogo} loading="lazy" alt="" />
                </div>
                <p className="btn-text">
                  <b>Continue with Google</b>
                </p>
              </div>
            )}
            {!iniFrame() && (
              <div className="auth-or-separator auth-or-separator--signup">
                <span>OR</span>
              </div>
            )}
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="signup__name-row">
                <div className="signup__name-col">
                  <div className="auth-form__label-row">
                    <div className="label-block">{translate('forms.common.first_name')}</div>
                    {!!errors.firstName?.message && (
                      <span className="auth-form__field-error">{errors.firstName.message}</span>
                    )}
                  </div>
                  <div className="auth-form__input-block">
                    <TextField
                      className="auth-mui-textfield"
                      required
                      placeholder={translate('forms.common.first_name')}
                      id="first-name"
                      {...register('firstName', {
                        required: translate('forms.common.required') as string,
                        minLength: {
                          value: MIN_INPUT_VALUE_LENGTH,
                          message: translate('forms.common.min_length'),
                        },
                        maxLength: {
                          value: MAX_INPUT_VALUE_LENGTH,
                          message: translate('forms.common.max_length50'),
                        },
                        validate: (value) => {
                          const urlPattern = /(?:https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-z]{2,})/gi;
                          return !urlPattern.test(value) || translate('forms.common.no_links');
                        },
                      })}
                      error={!!errors.firstName}
                    />
                  </div>
                </div>

                <div className="signup__name-col">
                  <div className="auth-form__label-row">
                    <div className="label-block">{translate('forms.common.last_name')}</div>
                    {!!errors.lastName?.message && (
                      <span className="auth-form__field-error">{errors.lastName.message}</span>
                    )}
                  </div>
                  <div className="auth-form__input-block">
                    <TextField
                      className="auth-mui-textfield"
                      required
                      placeholder={translate('forms.common.last_name')}
                      id="last-name"
                      {...register('lastName', {
                        required: translate('forms.common.required') as string,
                        minLength: {
                          value: MIN_INPUT_VALUE_LENGTH,
                          message: translate('forms.common.min_length'),
                        },
                        maxLength: {
                          value: MAX_INPUT_VALUE_LENGTH,
                          message: translate('forms.common.max_length50'),
                        },
                        validate: (value) => {
                          const urlPattern = /(?:https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-z]{2,})/gi;
                          return !urlPattern.test(value) || translate('forms.common.no_links');
                        },
                      })}
                      error={!!errors.lastName}
                    />
                  </div>
                </div>
              </div>
              <FormControl className="form-row signup__email-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.common.emailA')}</div>
                  {!!errors.email?.message && (
                    <span className="auth-form__field-error">{errors.email.message}</span>
                  )}
                </div>
                <div className="auth-form__input-block">
                  <TextField
                    className="auth-mui-textfield"
                    required
                    placeholder={translate('forms.common.emailA')}
                    id="email"
                    type="email"
                    inputProps={{maxLength: MAX_INPUT_VALUE_EMAIL_LENGTH}}
                    {...register('email', {
                      required: translate('forms.common.required') as string,
                      pattern: {
                        value: EMAIL_REGEX,
                        message: translate('forms.common.invalid_email'),
                      },
                    })}
                    error={!!errors.email}
                  />
                </div>
              </FormControl>

              <FormControl className="form-row signup__password-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.common.password')}</div>
                  {!!errors.password?.message && (
                    <span className="auth-form__field-error">{errors.password.message}</span>
                  )}
                </div>
                <div
                  className="auth-form__input-block"
                  ref={passwordAnchorRef}
                  onFocusCapture={() => setIsPasswordFocused(true)}
                  onBlurCapture={(e) => {
                    const next = e.relatedTarget as Node | null;
                    if (!e.currentTarget.contains(next)) {
                      setIsPasswordFocused(false);
                    }
                  }}
                >
                  <TextField
                    className="auth-mui-passfield"
                    required
                    placeholder={translate('forms.common.password')}
                    id="password"
                    type={visible.password ? 'text' : 'password'}
                    {...register('password', {
                      required: translate('forms.common.required') as string,
                      maxLength: {
                        value: MAX_INPUT_VALUE_LENGTH,
                        message: translate('forms.common.max_length50'),
                      },
                      pattern: {
                        value: PASSWORD_REGEX,
                        message: translate('forms.common.invalid_password'),
                      },
                    })}
                    error={!!errors.password || (passwordValue.length > 0 && notValidError)}
                    inputProps={{maxLength: MAX_INPUT_VALUE_LENGTH}}
                    InputProps={{
                      endAdornment:
                        <InputAdornment position="end">
                          <IsVisibleButton
                            isPrimary
                            onClick={handleClickShowPassword('password')}
                            isNowVisible={visible.password}
                          />
                        </InputAdornment>,
                    }}
                  />
                  <Popper
                    open={isPasswordFocused && passwordValue.length > 0}
                    anchorEl={passwordAnchorRef.current}
                    placement="top-start"
                    modifiers={[
                      {name: 'offset', options: {offset: [0, -12]}},
                    ]}
                    className="auth-password-rules-popper"
                  >
                    <Paper elevation={0} className="auth-password-rules-tooltip">
                      <ul className={passwordHelpListClassName}>
                        <li className={passwordValue.length > 5 ? 'success' : 'error'}>
                          6 characters minimum
                        </li>
                        <li className={
                          Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "dictionary")) ||
                          (Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "repeat")) && zxcvbnResult?.score === 0)
                            ? 'error'
                            : 'success'
                        }>
                          No dictionary words, repetitions and commonly used passwords
                        </li>
                        <li className={!/\s/.test(passwordValue) ? 'success' : 'error'}>
                          No spaces
                        </li>
                        <li className={/^(.*[0-9].*)$/.test(passwordValue) ? 'success' : 'error'}>
                          One number
                        </li>
                        <li className={/^(?=.*[a-z])(?=.*[A-Z]).*$/.test(passwordValue) ? 'success' : 'error'}>
                          At least one uppercase and one lowercase
                        </li>
                        {(!PASSWORD_REGEX.test(passwordValue) ||
                          Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "dictionary")) ||
                          (Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "repeat")) && zxcvbnResult?.score === 0)) && (
                          <li className={'error'}>
                            {translate('forms.common.invalid_password')}
                          </li>
                        )}
                      </ul>
                    </Paper>
                  </Popper>
                </div>
              </FormControl>

              <FormControl className="form-row signup__apps-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">Choose your first app</div>
                  {!!errors.appsProtect?.message && (
                    <span className="auth-form__field-error">{errors.appsProtect.message}</span>
                  )}
                </div>
                <div className="apps-container">
                  {EActivePlatformsNames.map((platform, index) => {
                    const isSelected = Boolean(appsProtect?.[index]);
                    return (
                      <button
                        key={platform}
                        type="button"
                        className={`signup__platform-item ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => togglePlatform(index)}
                        data-platform={platform}
                      >
                        {PLATFORM_ICONS[platform] && (
                          <img src={PLATFORM_ICONS[platform]} alt={platform} loading="lazy"/>
                        )}
                        <span>{platform}</span>
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <div className="signup__consent-list">
                <div className="signup__consent-block">
                  <div className="signup__consent-row" ref={termsConsentRef}>
                    <Checkbox
                      className={`signup__consent-checkbox ${errors.termsAccepted ? 'is-error' : ''}`}
                      icon={errors.termsAccepted ? <CheckboxInputError/> : <CheckboxInputEmpty/>}
                      checkedIcon={<CheckboxInputChecked/>}
                      {...register('termsAccepted', {
                        validate: (value) => value || 'Please accept the Terms of Service, SLA and Privacy Policy to continue.',
                      })}
                    />
                    <div className="signup__consent-text">
                      I agree to the{' '}
                      <a href="https://www.backuplabs.io/terms-of-use" className="link" target="_blank"
                         rel="noreferrer">
                        Terms of Service
                      </a>
                      ,{' '}
                      <a href="https://www.backuplabs.io/static-files/sla.pdf" className="link" target="_blank"
                         rel="noreferrer">
                        SLA
                      </a>
                      , and{' '}
                      <a href="https://www.backuplabs.io/privacy" className="link" target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>
                      .
                    </div>
                  </div>
                  <Popper
                    open={!!termsErrorMessage}
                    anchorEl={termsConsentRef.current}
                    placement="top-start"
                    modifiers={[
                      {name: 'offset', options: {offset: [-5, 8]}},
                    ]}
                    className="signup__consent-popper"
                  >
                    <div className="signup__consent-tooltip" role="alert">
                      <span className="signup__consent-tooltip__icon" aria-hidden>
                        <img src={errorStatusIcon} alt="" loading="lazy"/>
                      </span>
                      <span className="signup__consent-tooltip__text">{termsErrorMessage}</span>
                    </div>
                  </Popper>
                </div>

                <div className="signup__consent-block">
                  <div className="signup__consent-row">
                    <Checkbox
                      className="signup__consent-checkbox"
                      icon={<CheckboxInputEmpty/>}
                      checkedIcon={<CheckboxInputChecked/>}
                      {...register('marketingAccepted')}
                    />
                    <div className="signup__consent-text">
                      I agree to receive product updates and marketing communications.
                    </div>
                  </div>
                </div>
              </div>
              <div className="action-row signup__action-row">
                <Button
                  className="primary-button signup__get-started-btn"
                  type="submit"
                  disabled={isSubmit || notValidError || isGoogleAuth}
                >
                  <span className="auth-submit-inner auth-submit-inner--gap-sm">
                    {translate('common.default_pages.get_started')}
                    <img src={lineArrowRight} alt="" className="auth-submit-arrow" width={20} height={20}/>
                  </span>
                </Button>

                <div className="links-block center mt signup__action-links">
                  {translate('forms.sign_up.already_have_acc')}
                  <Link to="/auth/login" className="link ml-4">
                    {translate('common.default_pages.sign_in')}
                  </Link>
                </div>
              </div>

            </form>
          </Wrapper>
        </div>
        <div className='auth-signup-right'>
          <AuthRightAnimation/>
        </div>
      </div>
    </>
  );
};

export default SignUp;
